// 2026-05-26 - FormuLearning - 메인 페이지 (SCR-MAIN)
// 2026-09-01 수정 - 이슈 #58 S10(#70): 공통 화면 규칙 2건.
//   ① `M84-48` Step 3 을 넘어간 뒤(`current_step >= 4`) Step 1·2·3 을 **읽기 전용**으로
//      잠근다. 판정은 화면이 `current_step` 하나로 한다 — `evaluate_gating()` 은 전진
//      판정 전용이고, 역방향은 진행도 한 값이면 충분하다(같은 사실의 출처를 둘로 두지
//      않는다).
//   ② `M84-50` 프로젝트 진입 시 **진행 중 스텝만 열고** 나머지는 접는다. 종전은 완료
//      스텝만 접혔다 — 정반대였다. 열린 카드가 접힌 카드 밑에 묻히지 않도록 스크롤
//      영역 맨 위로 올린다(사용자 확정 Q-260901-02).
// 2026-09-24 수정 - 이슈 #59: Step 7 Final Report 추가. 각 Step Save and continue 직후 그 Step 의
//   보고서 절을 반영한다(설계 D-3). 반영 실패는 저장 흐름을 막지 않는다 — Step 7 재생성이 메운다.
// 2026-09-26 수정 - 이슈 #95 단계 3-5: 스텝 명칭 → `lib/steps`.
// 파일 위치: frontend/src/pages/MainPage.tsx

import { useCallback, useEffect, useRef, useState } from 'react'
import AppShell from '../components/layout/AppShell'
import CardShell from '../components/layout/CardShell'
import ErrorBoundary from '../components/feedback/ErrorBoundary'
import ProjectNewModal from '../features/project/ProjectNewModal'
import ProjectLoadModal from '../features/project/ProjectLoadModal'
import ProjectInfoModal from '../features/project/ProjectInfoModal'
import Step1Card from '../features/step1/Step1Card'
import Step2Card from '../features/step2/Step2Card'
import Step3Card from '../features/step3/Step3Card'
import Step4Card from '../features/step4/Step4Card'
import Step5Card from '../features/step5/Step5Card'
import Step6Card from '../features/step6/Step6Card'
import Step7Card from '../features/step7/Step7Card'
import { useProjectStore } from '../stores/projectStore'
import { useUiStore } from '../stores/uiStore'
import { useSaveBuffer } from '../stores/saveBufferStore'
import { useSummaryStore } from '../stores/summaryStore'
import { generateStep3Summary } from '../api/step3'
import { refreshReportStep } from '../api/report'
import { recordOperation } from '../api/operations'
import { STEP_LABELS } from '../lib/steps'

// 스텝 명칭은 `lib/steps` 가 가진다(이슈 #95). 여기서는 카드 머리 막대 색만 붙인다.
const BAR_COLORS: ('purple' | 'teal')[] = ['purple', 'teal', 'purple', 'purple', 'teal', 'teal', 'purple']
const STEPS = STEP_LABELS.map((title, i) => ({ number: i + 1, title, barColor: BAR_COLORS[i] }))

function StepContent({ stepNumber }: { stepNumber: number }) {
  if (stepNumber === 1) return <Step1Card />
  if (stepNumber === 2) return <Step2Card />
  if (stepNumber === 3) return <Step3Card />
  if (stepNumber === 4) return <Step4Card />
  if (stepNumber === 5) return <Step5Card />
  if (stepNumber === 6) return <Step6Card />
  if (stepNumber === 7) return <Step7Card />
  return null
}

function EmptyState() {
  return (
    <div className="flex h-full items-center justify-center">
      <div className="text-center">
        <p className="text-lg font-semibold text-text-primary">Welcome to FormuLearning</p>
        <p className="mt-2 text-sm text-text-secondary">
          Create a new project or select one from the left panel to begin.
        </p>
      </div>
    </div>
  )
}

// 2026-07-09 - 게이팅 안내 문구 (이슈 #33 REQ-B6). 하드 잠금·소프트 경고 대상 스텝별.
const HARD_LOCK: Record<number, string> = {
  3: 'Step 1에서 대조약을 먼저 선택하세요. 대조약을 선택해야 Target Design을 진행할 수 있습니다.',
}
// 2026-07-23 - Step 3 안내는 상시 노출 문제로 제거(이슈 #50 P1 검증 피드백 F-3).
const WARN_HINT: Record<number, string> = {
  1: '분자 식별(SMILES) 또는 대조약 선택이 필요합니다.',
  2: '물성 소스 채택 또는 QTPP 1행 이상이 필요합니다.',
  4: '실험 데이터 1건 이상과 최적화 완료가 필요합니다.',
  5: 'DoE 케이스 생성과 분석 실행이 필요합니다.',
  6: 'Reference·Test 입력 확인과 시뮬레이션 실행이 필요합니다.',  // 2026-09-24 #63: 코호트 폐지(BR-S6-10)
}

// 2026-09-01 - 역방향 잠금 안내 (이슈 #58 S10/#70, M84-48). Step 3을 넘어가면 이전 STEP은
// 값을 볼 수는 있으나 고칠 수 없다 — 학습이 끝난 설계 전제를 뒤늦게 바꾸면 이미 추천된
// 처방과 근거가 어긋난다.
const READONLY_HINT: Record<number, string> = {
  1: 'Step 4로 넘어간 뒤에는 Drug Profile을 수정할 수 없습니다. 값은 확인만 가능합니다.',
  2: 'Step 4로 넘어간 뒤에는 물성·QTPP를 수정할 수 없습니다. 값은 확인만 가능합니다.',
  // Step 3만 예외가 있다 — 대조약 Lot은 Step 4가 run마다 고르는 대상이라 계속 열어 둔다.
  3: 'Step 4로 넘어간 뒤에는 QTPP·총중량·X/Y 변수를 수정할 수 없습니다. 대조약(RLD) 실험 영역은 계속 입력·수정할 수 있습니다.',
}

// 2026-07-14 - 각 Step 헤더 직하 사용자 가이드 문구 (이슈 #23 F-12).
// Step 3·4는 PPT slide 3(그룹 2069·2106) 원문, Step 1·2·5·6은 회의록·이슈 기반 임시 1문장.
const STEP_GUIDE: Record<number, string[]> = {
  1: ['대상 의약품(주성분·대조약)을 검색·선택해 개발의 기준 정보를 확정하는 단계입니다.'],
  2: ['주성분의 물리화학적 특성과 목표품질(QTPP)을 정리하는 단계입니다.'],
  3: [
    'AI 모델이 기초 학습을 하기 위한 첫 데이터를 구성하는 단계입니다.',
    '❶ 변수 설정  ❷ 초기 실험점 생성  ❸ 시험 결과 입력',
  ],
  4: [
    '❶ 좌측의 추천 비율을 확인하고 [결과 입력 양식]을 다운로드합니다.',
    '❷ 해당 비율로 실제 실험을 진행한 뒤, 결과값을 엑셀에 기입하여 업로드합니다.',
    '❸ AI가 결과를 학습하여 더 나은 처방을 다시 추천합니다. (반복)',
    '❹ 목표 품질에 도달하면 [Stop Optimization]을 눌러 최적화를 종료합니다.',
  ],
  5: ['실험계획법(DoE)으로 요인의 영향과 최적 조건을 통계적으로 분석하는 단계입니다.'],
  6: ['대조약(Reference)과 시험 처방(Test)의 pH별 용출 profile을 같은 위장관 흡수 모델에 넣어 혈중농도 곡선을 비교하는 단계입니다.'],  // 2026-09-24 #63 아티팩트 문구
  7: ['Step 1~6 결과를 제형연구보고서(.docx)로 정리하는 단계입니다. 받은 문서에 실측 항목을 기입·수정해 완성합니다.'],  // 2026-09-24 #59
}

export default function MainPage() {
  const currentProject = useProjectStore((s) => s.currentProject)
  const gating = useProjectStore((s) => s.gating)
  const advanceStep = useProjectStore((s) => s.advanceStep)
  const setSyncStatus = useUiStore((s) => s.setSyncStatus)
  const setActiveStep = useUiStore((s) => s.setActiveStep)
  const flushStep = useSaveBuffer((s) => s.flushStep)
  const dirtySteps = useSaveBuffer((s) => s.dirtySteps)
  const summaries = useSummaryStore((s) => s.summaries)
  const setSummary = useSummaryStore((s) => s.setSummary)
  const [savingStep, setSavingStep] = useState<number | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  // 2026-09-01 - 이슈 #58 S10(#70, M84-50): 프로젝트 진입 시 진행 중 스텝 카드를 스크롤
  //   영역 맨 위로 올린다. 개폐만 뒤집으면 앞선 스텝의 접힌 카드가 위에 쌓여 정작 열린
  //   카드가 화면 밖에 남는다. 프로젝트 전환에서만 동작한다 — Save and continue 의 다음
  //   스텝 이동은 handleSaveAndContinue 가 이미 스크롤을 건다.
  const loadedProjectId = currentProject?.id ?? null
  useEffect(() => {
    if (!loadedProjectId) return
    // 카드는 프로젝트 id 를 key 로 리마운트되므로 이 시점에 DOM 이 아직 없을 수 있다.
    const timer = setTimeout(() => {
      const step = useProjectStore.getState().currentProject?.current_step
      if (!step) return
      document.getElementById(`step-${step}`)?.scrollIntoView({ behavior: 'auto', block: 'start' })
    }, 150)
    return () => clearTimeout(timer)
  }, [loadedProjectId])

  // 2026-07-09 - Save & Continue 실동작화 (이슈 #33): 기존 무저장 stub 해제.
  // Autosave OFF 지연 편집 flush → 진행도(current_step) 서버 확정(단조증가) → 좌측 점등 갱신 → 다음 스텝.
  const handleSaveAndContinue = useCallback(
    async (stepNumber: number) => {
      // 2026-07-09 - 이슈 #33 5단계: 스텝별 오퍼레이션 코드로 분리(step{N}_save).
      recordOperation(`step${stepNumber}_save`, currentProject?.id, { step: stepNumber })
      setSavingStep(stepNumber)
      setSyncStatus('saving')
      let saved = false
      try {
        await flushStep(stepNumber) // 이슈 #33 4단계: OFF 모드 지연 편집 일괄 PUT
        // 2026-07-18 - 이슈 #40 REQ-UX-70: Step3 저장(사용자 액션) 시 AI Summary 생성·영구 저장 후 헤더 발행.
        //   (2026-09-24 #59: 보고서 5.2 절이 이 요약을 인용하므로 절 반영보다 먼저 만든다)
        if (stepNumber === 3 && currentProject?.id) {
          try {
            const s = await generateStep3Summary(currentProject.id)
            setSummary(3, s.ai_summary, s.ai_summary_ko)
          } catch { /* 요약 실패는 저장 흐름을 막지 않는다 */ }
        }
        // 2026-09-24 - 이슈 #59: flush 된 저장값 기준으로 그 Step 의 보고서 절을 반영한다.
        //   advanceStep 이 게이팅을 갱신하기 전에 끝내야 Step 7 카드가 새 상태를 읽는다 — 그래서 await.
        if (currentProject?.id) {
          await refreshReportStep(currentProject.id, stepNumber).catch(() => { /* Step 7 재생성이 메운다 */ })
        }
        await advanceStep(stepNumber)
        setSyncStatus('synced')
        saved = true
      } catch {
        setSyncStatus('idle')
      } finally {
        setSavingStep(null)
      }
      // 2026-09-26 - 이슈 #96: 저장·진행도 반영이 실패하면 다음 스텝으로 넘어가지 않는다(useDeferredSave 규약).
      if (!saved) return
      setTimeout(() => setSyncStatus('idle'), 2000)

      const nextStep = stepNumber + 1
      if (nextStep <= 7) {
        setActiveStep(nextStep)
        setTimeout(() => {
          const el = document.getElementById(`step-${nextStep}`)
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }, 100)
      }
    },
    [setSyncStatus, setActiveStep, currentProject, advanceStep, flushStep, setSummary],
  )

  return (
    <AppShell>
      {currentProject ? (
        <div ref={scrollRef} className="mx-auto flex max-w-4xl flex-col gap-3.5">
          {STEPS.map((step) => {
            // 2026-07-09 - 게이팅 (이슈 #33 REQ-B6): Step3는 RLD 미선택 시 하드 잠금,
            // 그 외는 완료조건 미충족 시 소프트 경고(진입 허용).
            const locked = step.number === 3 && gating && !gating.rld_selected ? HARD_LOCK[3] : undefined
            const incomplete = gating ? gating.completion[String(step.number)] === false : false
            const warn = !locked && incomplete ? WARN_HINT[step.number] : undefined
            // 2026-09-01 - 이슈 #58 S10(#70, M84-48): Step 3을 넘어가면 이전 STEP은 읽기 전용.
            // 하드 잠금(locked)이 걸린 카드에는 겹쳐 걸지 않는다 — 그쪽은 내용 자체가 없다.
            const readOnly = !locked && currentProject.current_step >= 4 && step.number <= 3
              ? READONLY_HINT[step.number]
              : undefined
            // 2026-09-01 - 이슈 #58 S10(#70, M84-50): **진행 중 스텝만 펼친다.**
            // ~~종전: `step.number < current_step` — 완료 스텝만 접혔다(2026-07-18)~~ 요건과 반대였다.
            const defaultCollapsed = step.number !== currentProject.current_step
            return (
              // key에 프로젝트 id를 포함해 프로젝트 전환 시 카드를 리마운트 → 초기 접힘 상태 재계산.
              <ErrorBoundary key={`${currentProject.id}-${step.number}`} name={`Step ${step.number} - ${step.title}`}>
                <CardShell
                  stepNumber={step.number}
                  title={step.title}
                  barColor={step.barColor}
                  onSave={step.number <= 6 ? () => handleSaveAndContinue(step.number) : undefined}
                  saving={savingStep === step.number}
                  locked={locked}
                  warn={warn}
                  readOnly={readOnly}
                  // Step 3만 스스로 범위를 정한다 — 대조약 Lot 영역은 잠금에서 뺀다
                  // (사용자 확정 260901, `Step3Card`가 잠글 부분을 쥔다).
                  readOnlyScope={step.number === 3 ? 'self' : 'card'}
                  dirty={dirtySteps[step.number] ?? false}
                  guide={STEP_GUIDE[step.number]}
                  defaultCollapsed={defaultCollapsed}
                  summaryEn={summaries[step.number]?.en}
                  summaryKo={summaries[step.number]?.ko}
                >
                  <StepContent stepNumber={step.number} />
                </CardShell>
              </ErrorBoundary>
            )
          })}
        </div>
      ) : (
        <EmptyState />
      )}
      <ProjectNewModal />
      <ProjectLoadModal />
      <ProjectInfoModal />
    </AppShell>
  )
}
